<template>
  <div class="container-fluid">
    <md-card>
      <md-card-header>
        <div class="md-title">
          Labourer Assignment Report
        </div>
      </md-card-header>

      <md-card-content>

        <!-- Filters -->
        <div class="filters-row">
          <md-field>
            <div class="filter-item">
              <label class="filter-label">Vendor</label>

              <v-select
                  v-model="filters.vendor"
                  :options="vendors"
                  label="name"
                  :reduce="item => item.id"
                  placeholder="All Vendors"
                  clearable
              />
            </div>
          </md-field>
          <md-field>
            <div class="filter-item">
              <label class="filter-label">Department</label>

              <v-select
                  v-model="filters.department"
                  :options="departments"
                  label="name"
                  :reduce="item => item.id"
                  placeholder="All Departments"
                  clearable
              />
            </div>
          </md-field>
          <md-field>
            <div class="filter-item">
              <label class="filter-label">Guidance</label>

              <v-select
                  v-model="filters.guidance"
                  :options="guidances"
                  label="guidance_number"
                  :reduce="item => item.id"
                  :disabled="!filters.department"
              />
            </div>
          </md-field>

          <md-field>
            <div class="filter-item">
              <label class="filter-label">Shift</label>

              <v-select
                  v-model="filters.shift"
                  :options="shifts"
                  label="name"
                  :reduce="item => item.id"
                  :disabled="!filters.guidance"
              />
            </div>
          </md-field>

          <md-field>
            <div class="filter-item">
              <label class="filter-label">Workplace</label>

              <v-select
                  v-model="filters.workplace"
                  :options="workplaces"
                  label="name"
                  :reduce="item => item.id"
                  :disabled="!filters.shift"
              />
            </div>
          </md-field>

          <md-datepicker
              v-model="filters.shift_date"
              md-immediately
              :md-clearable="false"
              :disabled="true"
          >
            <label>Start Date</label>
          </md-datepicker>

          <md-datepicker
              v-model="filters.end_date"
              md-immediately
              :md-clearable="false"
              :disabled="true"
          >
            <label>End Date</label>
          </md-datepicker>

          <md-field>
            <div class="filter-item">
              <label class="filter-label">Assignment Status</label>
          <v-select
              v-model="filters.has_labourer_name"
              :options="nameOptions"
              label="label"
              :reduce="option => option.value"
              placeholder="Assignment"
          />
            </div>
          </md-field>

          <md-button
              class="md-raised md-primary"
              @click="resetFilters"
          >
            Reset
          </md-button>
          <!--New PDF Button-->
          <md-button
              class="md-primary"
              :disabled="generatingPdf || pdfReady"
              @click="generatePdf">

            <md-progress-spinner
                v-if="generatingPdf"
                md-mode="indeterminate"
                :md-diameter="18"
                :md-stroke="2"/>

            {{ generatingPdf ? 'Generating...' : 'Generate PDF' }}

          </md-button>

          <md-button
              class="md-info ml-2"
              :disabled="!pdfReady"
              @click="previewPdf">

            <md-icon>visibility</md-icon>

            Preview

          </md-button>

          <md-button
              class="md-success ml-2"
              :disabled="!pdfReady"
              @click="downloadPdf">

            <md-icon>download</md-icon>

            Download

          </md-button>

        </div>
        <div
            v-if="downloading"
            class="mt-3">

          <md-progress-bar
              md-mode="indeterminate">
          </md-progress-bar>

          <div class="text-muted mt-2">

            Loading PDF...

          </div>

        </div>

        <div
            v-if="generatingPdf"
            class="mt-3">

          <md-progress-bar
              md-mode="indeterminate">
          </md-progress-bar>

          <div class="text-muted mt-2">

            Generating Shift Assignment Report...

          </div>

        </div>
        <hr />
        <div v-if="pdfPreviewUrl" class="pdf-container" style="margin-top: 20px;">
          <md-button
              class="md-icon-button md-dense close-btn"
              @click="pdfPreviewUrl = null"
              title="Close Preview"
          >
            <i class="material-icons">close</i>
          </md-button>
          <iframe
              :src="pdfPreviewUrl"
              style="width: 100%; height: 90vh; border: 1px solid #ccc; border-radius: 8px;"
          ></iframe>
          <md-button class="md-accent" @click="pdfPreviewUrl = null">
            Close Preview
          </md-button>
        </div>
        <!-- Table -->
        <div class="table-responsive mt-3">
          <md-field style="width:120px">
            <label>Per Page</label>
            <md-select v-model="pagination.perPage">
              <md-option
                  v-for="size in pagination.perPageOptions"
                  :key="size"
                  :value="size"
              >
                {{ size }}
              </md-option>
            </md-select>
          </md-field>

          <div class="report-header">
            <h3>Labourer Assignment Report</h3>

            <div class="report-filters">
              <div><strong>Vendor:</strong> {{ selectedVendor }}</div>
              <div><strong>Department:</strong> {{ selectedDepartment }}</div>
              <div><strong>Guidance:</strong> {{ selectedGuidance }}</div>
              <div><strong>Shift:</strong> {{ selectedShift }}</div>
              <div><strong>Workplace:</strong> {{ selectedWorkplace }}</div>
              <div><strong>Shift Dates:</strong> {{ selectedShiftDate }}-{{ selectedEndDate }}</div>
              <div><strong>Assignment Status:</strong> {{ selectedAssignmentStatus }}</div>
              <div><strong>Total:</strong> {{ total}}</div>
            </div>
          </div>
          <table class="table table-bordered table-striped report-table">
            <thead>
            <tr>
              <th>#</th>
              <th>Labourer Name</th>
              <th>Take-On Number</th>
              <th>Department</th>
              <th>Workplace</th>
              <th>Activity</th>
              <th>Shift</th>
              <th>Shift Date</th>
            </tr>
            </thead>

            <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center">
                <md-progress-spinner
                    md-mode="indeterminate"
                    :md-diameter="30">
                </md-progress-spinner>

                <div class="mt-2">
                  Searching records...
                </div>
              </td>
            </tr>
            <tr
                v-else
                v-for="(item, index) in labourers"
                :key="item.id"
            >
              <td>{{ index + 1 }}</td>
              <td>{{ item.labourer ? item.labourer.name : '' }}</td>
              <td>{{ item.takeon_number}}</td>
              <td>{{ getDepartmentName(item) }}</td>
              <td>{{ getWorkplaceName(item) }}</td>
              <td>{{ getActivityName(item) }}</td>
              <td>{{ item.shift ? item.shift.name : '' }}</td>
              <td>
                {{ item.schedule ? formatDate(item.schedule.shift_date) : '' }}
              </td>
            </tr>

            <tr  v-if="!loading && !labourers.length">
              <td colspan="8" class="text-center">
                No records found.
              </td>
            </tr>

            </tbody>
          </table>

          <div class="d-flex justify-content-between align-items-center mt-3">

            <div>
              Showing {{ from + 1 }} - {{ to }} of {{ total }}
            </div>
            <div class="d-flex justify-content-between align-items-center mt-3">

              <pagination
                  v-model="pagination.currentPage"
                  :per-page="pagination.perPage"
                  :total="total"
                  @input="getList"
              />

            </div>

          </div>
        </div>

      </md-card-content>
    </md-card>

  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { Badge, Pagination } from '@/components';
import _ from "lodash";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
export default {
  name: 'LabourerAssignmentReport',
  components: {
    vSelect,
    pagination: Pagination,
    Badge
  },

  data () {
    return {
      generatingPdf: false,

      pdfReady: false,
      reportId:null,
      pdfPreviewUrl: null,
      downloading:false,
      filters: {
        department: '',
        workplace: '',
        guidance: '',
        shift: '',
        vendor: '',
        shift_date: new Date(),
        end_date: new Date(),
        has_labourer_name: 'Assigned Only'
      },
      nameOptions: [
        { label: 'All', value: null },
        { label: 'Assigned Only', value: 'Assigned Only' },
        { label: 'Not Assigned Only', value: "Not Assigned Only" },
        { label: 'Supervisor Checkout', value: "supervisor_checkout_at" }
      ],
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50,100,200,500,1000],
      },
      total: 0,
      sortation: {
        field: 'name',
        order: 'asc',
      },
      departments: [],
      workplaces: [],
      guidances: [],
      shifts: [],
      vendors: [],
      loading: false,

      labourers: [],
      exporting: false,
      exportProgress: 0,
    };
  },
  created () {
    this.getVendors();
    this.getDepartments();
    this.getList();
    this.reportId = localStorage.getItem('shiftAssignmentReportId');
    this.pdfReady = !!this.reportId;
  },
  watch: {
    'pagination.currentPage' () {
      this.getList();
    },

    'pagination.perPage' () {
      this.pagination.currentPage = 1;
      this.getList();
    },
    async 'filters.vendor' (vendorId) {

      this.filters.department = '';
      this.filters.guidance = '';
      this.filters.shift = '';
      this.filters.workplace = '';

      this.guidances = [];
      this.shifts = [];
      this.workplaces = [];

      if (vendorId) {
        this.invalidateReport();
        await this.getDepartments(vendorId);
      } else {
        this.invalidateReport();
        await this.getDepartments(); // Load all departments
      }

      this.getListDebounced();
    },
    'filters.department': async function (departmentId) {
      this.filters.guidance = '';
      this.filters.shift = '';
      this.filters.workplace = '';

      this.guidances = [];
      this.shifts = [];
      this.workplaces = [];

      if (departmentId) {
        this.invalidateReport();
        await this.getGuidances(departmentId);
      }

      this.getListDebounced();
    },

    'filters.guidance': async function (guidanceId) {
      this.filters.shift = '';
      this.filters.workplace = '';

      this.shifts = [];
      this.workplaces = [];

      if (guidanceId) {
        this.invalidateReport();
        await this.getShifts(guidanceId);
      }

      this.getListDebounced();
    },

    'filters.shift': async function (shiftId) {
      this.filters.workplace = '';
      this.workplaces = [];

      if (shiftId) {
        this.invalidateReport();
        await this.getWorkplaces(shiftId);
      }

      this.getListDebounced();
    },

    'filters.workplace'() {
      this.invalidateReport();
      this.getListDebounced();
    },

    'filters.shift_date'() {
      this.invalidateReport();
      this.getListDebounced();
    },
    'filters.end_date'() {
      this.invalidateReport();
      this.getListDebounced();
    },

    'filters.has_labourer_name'() {
      this.invalidateReport();
      this.pagination.currentPage = 1;
      this.getListDebounced();
    },

  },

  computed: {
    sort () {
      if (this.sortation.order === 'desc') {
        return `-${this.sortation.field}`;
      }
      return this.sortation.field;
    },

    from () {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to () {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    selectedVendor () {
      const item = this.vendors.find(
          v => v.id === this.filters.vendor
      );

      return item ? item.name : 'All Vendors';
    },
    selectedDepartment () {
      const item = this.departments.find(
          d => d.id === this.filters.department
      );

      return item ? item.name : 'All Departments';
    },

    selectedGuidance () {
      const item = this.guidances.find(
          g => g.id === this.filters.guidance
      );

      return item ? item.guidance_number : 'All Guidances';
    },

    selectedShift () {
      const item = this.shifts.find(
          s => s.id === this.filters.shift
      );

      return item ? item.name : 'All Shifts';
    },

    selectedWorkplace () {
      const item = this.workplaces.find(
          w => w.id === this.filters.workplace
      );

      return item ? item.name : 'All Workplaces';
    },

    selectedShiftDate () {
      return this.filters.shift_date
          ? this.formatDate(this.filters.shift_date)
          : 'All Dates';
    },
    selectedEndDate () {
      return this.filters.end_date
          ? this.formatDate(this.filters.end_date)
          : 'All Dates';
    },
    selectedAssignmentStatus () {
      return this.filters.has_labourer_name
          ? this.filters.has_labourer_name
          : 'All Assigned and Not Assigned';
    },
    // filteredRecords() {
    //   if (this.filters.has_labourer_name === null) {
    //     return this.labourers;
    //   }

    //   return this.labourers.filter(record => {
    //     const hasName =
    //         record.labourer &&
    //         record.labourer.name &&
    //         record.labourer.name.trim() !== '';
    //
    //     return this.filters.has_labourer_name === 'Assigned Only'
    //         ? hasName
    //         : !hasName;
    //   });
    // }

  },

  methods: {
    invalidateReport() {

      this.pdfReady = false;
      this.reportId = null;
      this.generatingPdf= false;
      this.downloading= false;

    },
    async getVendors () {
      await this.$store.dispatch('vendors/list');
      this.vendors =
          this.$store.getters['vendors/dropdown'];
    },
    getDepartmentName(item) {
      return item &&
      item.schedule &&
      item.schedule.activityShift &&
      item.schedule.activityShift.activityGuidance &&
      item.schedule.activityShift.activityGuidance.guidance &&
      item.schedule.activityShift.activityGuidance.guidance.department
          ? item.schedule.activityShift.activityGuidance.guidance.department.name
          : '';
    },
    getWorkplaceName(item) {
      return item &&
      item.schedule &&
      item.schedule.activityShift &&
      item.schedule.activityShift.activityGuidance &&
      item.schedule.activityShift.activityGuidance.guidanceWorkplace &&
      item.schedule.activityShift.activityGuidance.guidanceWorkplace.workplace
          ? item.schedule.activityShift.activityGuidance.guidanceWorkplace.workplace.name
          : '';
    },
    getActivityName(item) {
      return item &&
      item.schedule &&
      item.schedule.activityShift &&
      item.schedule.activityShift.activityGuidance &&
      item.schedule.activityShift.activityGuidance.activity
          ? item.schedule.activityShift.activityGuidance.activity.name
          : '';
    },
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),
    async getList () {
      this.loading = true;
      let params = {
        include: 'labourer,' +
            'labourerAttendance,' +
            'workplace,' +
            'activity,' +
            'schedule,' +
            'shift,' +
            'schedule.activityShift.activityGuidance.guidance.department,' +
            'schedule.activityShift.activityGuidance.guidanceWorkplace.workplace,'+
            'schedule.activityShift.activityGuidance.activity',
        ...(this.sort ? { sort: this.sort } : {}),
        filter: {
          ...(this.filters.vendor ? { vendor: this.filters.vendor } : {}),
          ...(this.filters.department ? { department: this.filters.department } : {}),
          ...(this.filters.guidance ? { guidance: this.filters.guidance } : {}),
          ...(this.filters.shift ? { shift: this.filters.shift } : {}),
          ...(this.filters.workplace ? { workplace: this.filters.workplace } : {}),
          ...(this.filters.shift_date ? { shift_date: this.formatFilterDate(this.filters.shift_date) } : {}),
          ...(this.filters.end_date ? { end_date: this.formatFilterDate(this.filters.end_date) } : {}),
          ...(this.filters.has_labourer_name
              ? { has_labourer_name: this.filters.has_labourer_name }
              : {})

        },
        page: {
          number: this.pagination.currentPage,
          size: this.pagination.perPage,
        },
      };

      try {
        await this.$store.dispatch('labourerSchedules/list', params);
        this.labourers = this.$store.getters['labourerSchedules/list'];

        // this.takeonStatus()
        this.total = this.$store.getters['labourerSchedules/listTotal'];
      } catch (e) {
        this.notifyException(e);
      }finally {

        this.loading = false;

      }
    },
    async getShifts (guidanceId) {
      const params = {
        filter: {
          guidance: guidanceId
        }
      };

      await this.$store.dispatch(
          'shifts/list',
          params
      );

      this.shifts =
          this.$store.getters['shifts/dropdown'];
    },

    async getWorkplaces (shiftId) {
      const params = {
        filter: {
          shift: shiftId,
          guidance:this.filters.guidance
        }
      };

      await this.$store.dispatch(
          'workplaces/list',
          params
      );

      this.workplaces =
          this.$store.getters['workplaces/dropdown'];
    },
    async getGuidances (departmentId) {
      const params = {
        filter: {
          department: departmentId
        }
      };

      await this.$store.dispatch(
          'guidances/list',
          params
      );

      this.guidances =
          this.$store.getters['guidances/dropdown'];
    },
    async getDepartments (vendorId = null) {

      const params = {
        filter: {
          ...(vendorId ? { vendor: vendorId } : {}),
        },
      };

      await this.$store.dispatch(
          'departments/list',
          params
      );

      this.departments =
          this.$store.getters['departments/dropdown'];
    },
    formatDate (date) {
      if (!date) {
        return '';
      }

      const d = new Date(date);

      const months = [
        'Jan', 'Feb', 'Mar', 'Apr',
        'May', 'Jun', 'Jul', 'Aug',
        'Sep', 'Oct', 'Nov', 'Dec'
      ];

      return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },

    formatFilterDate (date) {
      if (!date) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

   async resetFilters () {
      this.filters = {
        vendor: '',
        department: '',
        workplace: '',
        guidance: '',
        shift: '',
        shift_date: new Date(),
        has_labourer_name: 'Assigned Only'
      };
      this.guidances = [];
      this.shifts = [];
      this.workplaces = [];

      await this.getDepartments(); // Reload all departments

      this.getList();
    },


    async exportPdf () {

      try {
        this.exporting = true;
        this.exportProgress = 10;

        await this.$nextTick();
        this.exportProgress = 30;
        const params = {
          include:
              'labourer,' +
              'labourerAttendance,' +
              'workplace,' +
              'activity,' +
              'schedule,' +
              'shift,' +
              'schedule.activityShift.activityGuidance.guidance.department,' +
              'schedule.activityShift.activityGuidance.guidanceWorkplace.workplace,' +
              'schedule.activityShift.activityGuidance.activity',

          ...(this.sort ? { sort: this.sort } : {}),

          filter: {
            ...(this.filters.vendor ? { vendor: this.filters.vendor } : {}),
            ...(this.filters.department ? { department: this.filters.department } : {}),
            ...(this.filters.guidance ? { guidance: this.filters.guidance } : {}),
            ...(this.filters.shift ? { shift: this.filters.shift } : {}),
            ...(this.filters.workplace ? { workplace: this.filters.workplace } : {}),
            ...(this.filters.shift_date ? {
                  shift_date: this.formatFilterDate(
                      this.filters.shift_date
                  )
                } : {}),
            ...(this.filters.end_date ? {
                  end_date: this.formatFilterDate(
                      this.filters.end_date
                  )
                } : {}),
            ...(this.filters.has_labourer_name ? { has_labourer_name: this.filters.has_labourer_name } : {})
          },

          page: {
            number: 1,
            size: this.total || 10000
          }
        };
        console.log('Starting export');
        await this.$store.dispatch(
            'labourerSchedules/list',
            params
        );
        console.log('Records loaded');
        const labourers =
            this.$store.getters['labourerSchedules/list'];
        console.log('Total records:', labourers.length);

        await this.$nextTick();
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });
        console.log('PDF created');


        this.exportProgress = 50;
        // Report Header
        doc.setFontSize(16);

        doc.text(
            'LABOURER ASSIGNMENT REPORT',
            doc.internal.pageSize.width / 2,
            15,
            { align: 'center' }
        );

        doc.setFontSize(10);

        let y = 25;

        doc.text(
            `Vendor: ${this.selectedVendor}`,
            14,
            y
        );

        y += 6;
        doc.text(
            `Department: ${this.selectedDepartment}`,
            14,
            y
        );

        y += 6;

        doc.text(
            `Guidance: ${this.selectedGuidance}`,
            14,
            y
        );

        y += 6;

        doc.text(
            `Shift: ${this.selectedShift}`,
            14,
            y
        );

        y += 6;

        doc.text(
            `Workplace: ${this.selectedWorkplace}`,
            14,
            y
        );

        y += 6;

        doc.text(
            `Shift Dates: ${this.selectedShiftDate} - ${this.selectedEndDate}`,
            14,
            y
        );

        y += 6;

        doc.text(
            `Assignment Status: ${this.selectedAssignmentStatus}`,
            14,
            y
        );

        y += 10;

        autoTable(doc, {
          startY: y,

          head: [[
            '#',
            'Labourer',
            'Take-On',
            'Department',
            'Workplace',
            'Activity',
            'Shift',
            'Shift Date',
            'Amount(TZS)',
            'Signature',
          ]],

          body: labourers.map((item, index) => [
            index + 1,
            item.labourer?item.labourer.name : '',
            item.takeon_number || '',
            this.getDepartmentName(item),
            this.getWorkplaceName(item),
            this.getActivityName(item),
            item.shift?.name || '',
            item.schedule ? this.formatDate(item.schedule.shift_date) : ''],
          '...................',
          '...................'),

          theme: 'grid',

          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: 'linebreak'
          },

          headStyles: {
            fillColor: [0, 102, 204],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle'
          },

          columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 30 },
            2: { cellWidth: 30 },
            3: { cellWidth: 35 },
            4: { cellWidth: 25 },
            5: { cellWidth: 55 },
            6: { cellWidth: 20 },
            7: { cellWidth: 22 },
            8: { cellWidth: 22 },
            9: { cellWidth: 22 }
          }
        });

        // Footer
        const pageCount =
            doc.internal.getNumberOfPages();

        const generatedAt =
            new Date().toLocaleString();

        for (let i = 1; i <= pageCount; i++) {

          doc.setPage(i);

          doc.setFontSize(8);

          doc.text(
              `Generated: ${generatedAt}`,
              14,
              doc.internal.pageSize.height - 10
          );

          doc.text(
              `Total Records: ${labourers.length}`,
              80,
              doc.internal.pageSize.height - 10
          );

          doc.text(
              `Page ${i} of ${pageCount}`,
              doc.internal.pageSize.width - 35,
              doc.internal.pageSize.height - 10
          );
        }
        console.log('Table created');
        this.exportProgress = 90;

        await this.$nextTick();

        doc.save('labourer-assignment-report.pdf');
        console.log('PDF saved');
        this.exportProgress = 100;

      } catch (e) {

        this.notifyException(e);

      }finally {

        setTimeout(() => {
          this.exporting = false;
          this.exportProgress = 0;
        }, 500);

      }
    },
    async generatePdf() {
      try {

        this.generatingPdf = true;
        this.pdfReady = false;

        const response = await this.$store.dispatch(
            'labourerSchedules/generateShiftAssignmentPdf',
            {
              filters: this.filters
            }
        );

        this.reportId = response.report_id;

        this.waitForPdf();

      } catch (e) {

        this.generatingPdf = false;

        this.notifyException(e);

      }
    },
    // async waitForPdf() {
    //
    //   const timer = setInterval(async () => {
    //
    //     try {
    //
    //       const response = await this.$store.dispatch(
    //           'labourerSchedules/shiftAssignmentPdfStatus',
    //           {
    //             reportId: this.reportId
    //           }
    //       );
    //
    //       console.log("Status")
    //       console.log(response.status)
    //       if (response.status === 'COMPLETED') {
    //
    //         clearInterval(timer);
    //
    //         this.generatingPdf = false;
    //         this.pdfReady = true;
    //
    //       } else if (response.status === 'FAILED') {
    //
    //         clearInterval(timer);
    //
    //         this.generatingPdf = false;
    //         this.pdfReady = false;
    //
    //         this.notifyError(response.error);
    //
    //       }
    //
    //     } catch (e) {
    //
    //       clearInterval(timer);
    //
    //       this.generatingPdf = false;
    //
    //       this.notifyException(e);
    //
    //     }
    //
    //   }, 5000);
    //
    // },
    async waitForPdf() {
      try {
        const response = await this.$store.dispatch(
            'labourerSchedules/shiftAssignmentPdfStatus',
            {
              reportId: this.reportId
            }
        );

        console.log('PDF Status:', response.status);

        switch (response.status) {
          case 'COMPLETED':
            this.generatingPdf = false;
            this.pdfReady = true;
            return;

          case 'FAILED':
            this.generatingPdf = false;
            this.pdfReady = false;
            this.notifyError(
                response.message || 'PDF generation failed.'
            );
            return;

          case 'NOT_FOUND':
            this.generatingPdf = false;
            this.pdfReady = false;
            this.notifyError(
                response.message || 'Report status not found or expired.'
            );
            return;

          case 'QUEUED':
          case 'PROCESSING':
            setTimeout(() => {
              this.waitForPdf();
            }, 2000);
            return;

          default:
            this.generatingPdf = false;
            this.pdfReady = false;
            this.notifyError(
                `Unknown PDF status: ${response.status}`
            );
        }
      } catch (e) {
        this.generatingPdf = false;
        this.pdfReady = false;

        this.notifyException(e);
      }
    },

    async previewPdf() {
this.downloading=true;
      try {

        const response = await this.$store.dispatch(
            'labourerSchedules/previewShiftAssignmentPdf',
            {
              reportId: this.reportId
            }
        );

        // Release previous object URL
        if (this.pdfPreviewUrl) {
          URL.revokeObjectURL(this.pdfPreviewUrl);
        }

        // Create a new Blob URL
        const blob = new Blob(
            [response.data],
            { type: 'application/pdf' }
        );

        this.pdfPreviewUrl = URL.createObjectURL(blob);

      } catch (e) {

        this.notifyException(e);

      }finally {
        this.downloading=false;
      }

    },
    async downloadPdf() {
      this.downloading=true;
      try {

        const response = await this.$store.dispatch(
            'labourerSchedules/downloadShiftAssignmentPdf',
            {
              reportId: this.reportId
            }
        );

        const blob = new Blob(
            [response.data],
            {
              type: 'application/pdf'
            }
        );

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');

        link.href = url;
        link.download = 'ShiftAssignmentReport.pdf';

        document.body.appendChild(link);

        link.click();

        link.remove();

        window.URL.revokeObjectURL(url);

      } catch (e) {

        this.notifyException(e);

      }finally {
        this.downloading=false;
      }

    },
    // async downloadPdf() {
    //
    //   try {
    //
    //     const response = await this.$store.dispatch(
    //         'reports/downloadShiftAssignmentPdf',
    //         {
    //           filters: this.filters
    //         }
    //     );
    //
    //     const blob = new Blob(
    //         [response.data],
    //         {
    //           type: 'application/pdf'
    //         }
    //     );
    //
    //     const link =
    //         document.createElement('a');
    //
    //     link.href =
    //         window.URL.createObjectURL(blob);
    //
    //     link.download =
    //         'ShiftAssignmentReport.pdf';
    //
    //     link.click();
    //
    //   } catch (e) {
    //
    //     this.notifyException(e);
    //
    //   }
    //
    // }
  }
};
</script>

<style scoped>
.table th {
  white-space: nowrap;
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  align-items: start;
}

.filter-item {
  width: 100%;
}

.vs__dropdown-menu {
  z-index: 9999 !important;
}
.report-header {
  text-align: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #ddd;
  padding-bottom: 15px;
}

.report-header h3 {
  margin-bottom: 10px;
  font-weight: 600;
}

.report-filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  font-size: 14px;
}

.report-filters div {
  min-width: 220px;
  text-align: left;
}
::v-deep .md-datepicker-input {
  pointer-events: none;
}
.export-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.search-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.report-table {
  width: 100%;
  table-layout: fixed;
  font-size: 12px;
}

.report-table th,
.report-table td {
  padding: 6px;
  vertical-align: middle;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.report-table th {
  white-space: normal;
  text-align: center;
  font-weight: 600;
}

.report-table td {
  white-space: normal;
}

.report-table th:nth-child(1),
.report-table td:nth-child(1) {
  width: 50px;
}

.report-table th:nth-child(2),
.report-table td:nth-child(2) {
  width: 180px;
}

.report-table th:nth-child(3),
.report-table td:nth-child(3) {
  width: 120px;
}

.report-table th:nth-child(4),
.report-table td:nth-child(4) {
  width: 140px;
}

.report-table th:nth-child(5),
.report-table td:nth-child(5) {
  width: 140px;
}

.report-table th:nth-child(6),
.report-table td:nth-child(6) {
  width: 180px;
}

.report-table th:nth-child(7),
.report-table td:nth-child(7) {
  width: 100px;
}

.report-table th:nth-child(8),
.report-table td:nth-child(8) {
  width: 120px;
}
</style>
